import type { FC } from 'react'

import { Image } from '@skytech/ui/Image'
import { Td } from '@skytech/ui/Table/Td'
import PailOutlineIcon from 'mdi-react/PailOutlineIcon'
import RecycleIcon from 'mdi-react/RecycleIcon'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import Highlight from '@/components/Highlight'
import { LocalDate } from '@/components/LocalDate'
import { RouterLink } from '@/components/RouterLink'
import { TrLink } from '@/components/TrLink'
import { useIsAdmin } from '@/hooks/access'
import type { Asset } from '@/modules/admin/resources'
import { APP_ROUTES } from '@/util/app-routes'
import { imagePlaceholder } from '@/util/misc'

import type { AssetUserType } from '../../resources'

import './AssetListItem.styles.scss'

export const AssetListItem: FC<
  Asset & {
    searchFilter: string
  }
> = ({
  costObjectName,
  imageUrl,
  assetDescription,
  id,
  searchFilter,
  costObjectId,
  received,
  cost,
  address,
  addressCity,
  addressZip,
  status,
  userType,
  imeiSnr,
  assetType,
}) => {
  const translate = useTranslation()

  return (
    <TrLink path={APP_ROUTES.HARDWARE_ASSET.replace(':id', String(id))}>
      <Td className="hidden md:table-cell">
        <div className="asset-name">
          <Image
            alt="asset"
            src={imageUrl || ''}
            className="hidden md:table-cell"
            onError={imagePlaceholder}
          />

          <AssetCategoryIcon userType={userType} />
        </div>
      </Td>

      <Td>
        <div>
          <Highlight pattern={searchFilter} text={assetDescription} />
        </div>
        <div className="subtitle is-mobile">
          <Highlight pattern={searchFilter} text={costObjectName} />
        </div>
        <div className="flex gap-1">
          <div className="subtitle hidden md:table-cell">
            <Highlight pattern={searchFilter} text={translate(`asset_type/${assetType}`)} />
          </div>
          <div className="subtitle hidden md:table-cell">
            <Highlight pattern={searchFilter} text={imeiSnr} />
          </div>
        </div>
      </Td>

      <Td className="hidden md:table-cell">
        <div>
          {costObjectName === 'Expired' ? (
            <Translation id="User unavailable" />
          ) : (
            <AssetUser
              searchFilter={searchFilter}
              costObjectName={costObjectName}
              costObjectId={costObjectId}
            />
          )}
        </div>
        <Highlight pattern={searchFilter} text={translate(`asset_user_type/${userType}`)} />
      </Td>

      <Td className="hidden md:table-cell">
        <Highlight pattern={searchFilter} text={translate(`asset_status/${status}`)} />
      </Td>

      <Td className="hidden md:table-cell">
        <Highlight pattern={searchFilter} text={address} />
        <div className="subtitle">
          <Highlight pattern={searchFilter} text={addressZip} />{' '}
          <Highlight pattern={searchFilter} text={addressCity} />
        </div>
      </Td>

      <Td className="hidden md:table-cell">
        <LocalDate date={received} />
      </Td>

      <Td className="hidden md:table-cell">
        <FormattedNumberWithCurrency amount={cost || 0} />
      </Td>
    </TrLink>
  )
}

const AssetCategoryIcon: FC<{ userType: AssetUserType }> = ({ userType }) => {
  if (!['GREENPOOL', 'POOL'].includes(userType)) return null

  return (
    <div className="duration-250 absolute bottom-0 right-0 flex h-7 w-7 items-center justify-center overflow-hidden rounded-full bg-white p-1 transition ease-in">
      {userType === 'GREENPOOL' && <RecycleIcon className="ml-1 fill-green-500" />}
      {userType === 'POOL' && <PailOutlineIcon className="ml-1 fill-green-500" />}
    </div>
  )
}

type AssetUserProps = {
  searchFilter: string
} & Pick<Asset, 'costObjectName' | 'costObjectId'>

const AssetUser: FC<AssetUserProps> = ({ searchFilter, costObjectId, costObjectName }) => {
  const isAdmin = useIsAdmin()

  if (isAdmin) {
    return (
      <RouterLink
        to={APP_ROUTES.EMPLOYEE.replace(':costObjectId', String(costObjectId))}
        onClick={e => {
          e.stopPropagation()
        }}
      >
        <Highlight pattern={searchFilter} text={costObjectName} />
      </RouterLink>
    )
  }
  return <Highlight pattern={searchFilter} text={costObjectName} />
}
